import { DOCUMENT } from '@angular/common';
import { Injectable, Injector, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  finalize,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { AuthService } from './auth.service';

import { ENVIRONMENT_APP_NAME } from '@qtek/core/api-core';
import {
  IMPERSONATION_EMAIL_KEY,
  IMPERSONATION_TOKEN_KEY,
  setImpersonationDomState,
} from '@qtek/shared/utils';
import { CoreStore, MetaStore, UserStore } from '../..';
import { AuthActions } from './auth.actions';

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private matDialog = inject(MatDialog);
  private document = inject<Document>(DOCUMENT);
  private envAppName = inject(ENVIRONMENT_APP_NAME);
  private injector = inject(Injector);
  private metaStore = inject(MetaStore);
  private coreStore = inject(CoreStore);
  private userStore = inject(UserStore);

  wsInitToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getWSInitToken),
      mergeMap(() =>
        this.authService.getWsSessionInitToken().pipe(
          map(({ res }) =>
            AuthActions.getWSInitTokenSuccess({ payload: res.wsTkn })
          ),
          catchError(() => of(AuthActions.getWSInitTokenError()))
        )
      )
    )
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      tap(() => this.coreStore.openLoading()),
      mergeMap(({ payload }) =>
        this.authService.login(payload).pipe(
          tap(res => {
            if (res.sts?.sts === 'WARN' && (!res.res.acn || !res.res.prs)) {
              this.router.navigate(['/']);
            } else {
              let { t } = this.route.snapshot.queryParams;
              const { returnUrl } = this.route.snapshot.queryParams;
              if (t && typeof t === 'string') {
                const decodeMap: any = {
                  '-': '+',
                  _: '/',
                  '.': '=',
                };

                t = t.replace(/[-_.]/g, ch => decodeMap[ch]);

                return this.router.navigateByUrl(decodeURIComponent(atob(t)));
              }
              this.router.navigateByUrl(returnUrl || '');
            }
            return null;
          }),
          mergeMap(res => {
            if (res.sts?.sts === 'WARN' || !res.res.acn || !res.res.prs) {
              return [AuthActions.loginOk()];
            }
            this.userStore.setUserData(res.res);
            return [AuthActions.loginOk()];
          }),
          catchError(() => {
            this.coreStore.closeLoading();
            return of(AuthActions.loginError());
          })
        )
      )
    )
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        concatMap(({ queryParams, skipRedirect }) =>
          this.authService.logout().pipe(
            finalize(() => {
              this.document.defaultView?.sessionStorage?.removeItem(
                IMPERSONATION_TOKEN_KEY
              );
              this.document.defaultView?.sessionStorage?.removeItem(
                IMPERSONATION_EMAIL_KEY
              );
              setImpersonationDomState(this.document, false);
              this.matDialog.closeAll();

              if (!skipRedirect) {
                this.router.navigate(
                  [this.envAppName === 'booking' ? '' : 'auth'],
                  {
                    queryParams: queryParams || null,
                    queryParamsHandling: 'merge',
                  }
                );
              }
            }),
            catchError(() => EMPTY)
          )
        )
      ),
    { dispatch: false }
  );

  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.register),
      exhaustMap(({ payload }) => {
        this.coreStore.openLoading();

        return this.authService.register(payload).pipe(
          concatMap(() =>
            this.authService.login({
              email: payload.email,
              passwd: payload.passwd,
              mfa_code: 'Test7777',
            })
          ),
          mergeMap(({ res }) => {
            this.userStore.setUserData(res);
            return [AuthActions.registerOk()];
          }),
          tap(() => {
            if (this.envAppName === 'booking') {
              toObservable(this.metaStore.guiDomain, {
                injector: this.injector,
              })
                .pipe(filter(Boolean), take(1))
                .subscribe(domain => (this.document.location.href = domain));
            } else {
              this.router.navigateByUrl(
                decodeURIComponent(
                  this.route.snapshot.queryParams['returnUrl'] || ''
                )
              );
            }
          }),
          catchError(() => {
            this.coreStore.closeLoading();
            return of(AuthActions.registerError());
          })
        );
      })
    )
  );

  resetPasswordEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPasswordEmail),
      switchMap(({ payload }) =>
        this.authService.sendResetPasswordEmail(payload).pipe(
          map(() => AuthActions.resetPasswordEmailSuccess()),
          catchError(() => of(AuthActions.resetPasswordEmailFailure()))
        )
      )
    )
  );
}
